// src/controllers/useCrmController.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { crmService, CRM_STAGES, CRM_PRIORITIES } from '../services/crmService';
import { useAuth } from './auth';

export const useCrmController = () => {
  const { user, adminProfile, logAction } = useAuth();

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [followups, setFollowups] = useState([]);

  // Views & Modals
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban', 'table', 'followups'
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadActivities, setLeadActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignFilter, setAssignFilter] = useState(
    adminProfile?.role === 'Sales Executive' ? 'mine' : 'all'
  );
  const [dateFilter, setDateFilter] = useState('all');

  // Form states for Lead Detail Drawer
  const [newNote, setNewNote] = useState('');
  const [activityType, setActivityType] = useState('call');
  const [savingNote, setSavingNote] = useState(false);

  // New Followup form
  const [newFollowupTitle, setNewFollowupTitle] = useState('');
  const [newFollowupDate, setNewFollowupDate] = useState('');
  const [savingFollowup, setSavingFollowup] = useState(false);

  // Load leads and staff
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, staffRes, followupsRes] = await Promise.all([
        crmService.getAllCrmLeads(),
        crmService.getStaffList(),
        crmService.getFollowups(),
      ]);

      setLeads(leadsRes.data || []);
      setStaffList(staffRes.data || []);
      setFollowups(followupsRes.data || []);
    } catch (err) {
      console.error('Error fetching CRM data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // If user role is Sales Executive, default assignFilter to 'mine'
  useEffect(() => {
    if (adminProfile?.role === 'Sales Executive') {
      setAssignFilter('mine');
    }
  }, [adminProfile]);

  // Load activities when a lead is opened
  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
    setNewNote('');
    setNewFollowupTitle('');
    setNewFollowupDate('');
    if (!lead) {
      setLeadActivities([]);
      return;
    }
    setActivitiesLoading(true);
    const { data } = await crmService.getLeadActivities(lead.lead_id);
    setLeadActivities(data || []);
    setActivitiesLoading(false);
  };

  // Filtered Leads
  const filteredLeads = useMemo(() => {
    let result = [...leads];

    // Search
    if (search.trim() !== '') {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.customer_name || '').toLowerCase().includes(q) ||
        (l.customer_phone || '').toLowerCase().includes(q) ||
        (l.city || '').toLowerCase().includes(q) ||
        (l.requirement || '').toLowerCase().includes(q) ||
        (l.property_title || '').toLowerCase().includes(q) ||
        (l.budget || '').toLowerCase().includes(q) ||
        (l.details || '').toLowerCase().includes(q)
      );
    }

    // Category / Source filter
    if (categoryFilter !== 'all') {
      result = result.filter(l => l.lead_category === categoryFilter);
    }

    // City filter
    if (cityFilter !== 'all') {
      result = result.filter(l => (l.city || '').toLowerCase() === cityFilter.toLowerCase());
    }

    // Stage / Status filter
    if (stageFilter !== 'all') {
      result = result.filter(l => (l.status || 'New').toLowerCase() === stageFilter.toLowerCase());
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(l => (l.priority || 'Warm').toLowerCase() === priorityFilter.toLowerCase());
    }

    // Assign filter
    if (assignFilter === 'mine') {
      result = result.filter(l => l.assigned_to === user?.id);
    } else if (assignFilter === 'unassigned') {
      result = result.filter(l => !l.assigned_to);
    } else if (assignFilter !== 'all') {
      result = result.filter(l => l.assigned_to === assignFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let cutoff = new Date();
      if (dateFilter === 'today') cutoff.setHours(0, 0, 0, 0);
      else if (dateFilter === 'week') cutoff.setDate(now.getDate() - 7);
      else if (dateFilter === 'month') cutoff.setMonth(now.getMonth() - 1);
      result = result.filter(l => new Date(l.created_at) >= cutoff);
    }

    return result;
  }, [leads, search, categoryFilter, cityFilter, stageFilter, priorityFilter, assignFilter, dateFilter, user]);

  // Kanban Stage Grouping
  const kanbanColumns = useMemo(() => {
    const columns = {};
    CRM_STAGES.forEach(stage => {
      columns[stage.key] = [];
    });

    filteredLeads.forEach(lead => {
      const rawStatus = lead.status || 'New';
      let targetKey = 'New';
      if (rawStatus.toLowerCase().includes('contact') || rawStatus === 'accepted') targetKey = 'Contacted';
      else if (rawStatus.toLowerCase().includes('visit')) targetKey = 'Visit scheduled';
      else if (rawStatus.toLowerCase().includes('close')) targetKey = 'Closed';
      else if (rawStatus.toLowerCase().includes('not') || rawStatus.toLowerCase().includes('reject')) targetKey = 'Not interested';
      else targetKey = 'New';

      if (!columns[targetKey]) columns[targetKey] = [];
      columns[targetKey].push(lead);
    });

    return columns;
  }, [filteredLeads]);

  // Today's follow-up reminders
  const todaysFollowups = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return followups.filter(f => !f.is_completed && new Date(f.due_date) <= today);
  }, [followups]);

  // Action: Update Stage / Status
  const handleUpdateStatus = async (leadId, sourceTable, nextStatus) => {
    const { error } = await crmService.updateLead(leadId, sourceTable, { status: nextStatus });
    if (error) {
      alert(error.message || 'Failed to update status');
      return;
    }

    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, status: nextStatus } : l));
    if (selectedLead && selectedLead.lead_id === leadId) {
      setSelectedLead(prev => ({ ...prev, status: nextStatus }));
      // Refresh activities
      const { data } = await crmService.getLeadActivities(leadId);
      setLeadActivities(data || []);
    }

    await logAction(user?.id, adminProfile?.full_name || 'Staff', 'CRM Status Update', leadId, `Status changed to "${nextStatus}"`);
  };

  // Action: Update Priority
  const handleUpdatePriority = async (leadId, sourceTable, nextPriority) => {
    const { error } = await crmService.updateLead(leadId, sourceTable, { priority: nextPriority });
    if (error) {
      alert(error.message || 'Failed to update priority');
      return;
    }

    setLeads(prev => prev.map(l => l.lead_id === leadId ? { ...l, priority: nextPriority } : l));
    if (selectedLead && selectedLead.lead_id === leadId) {
      setSelectedLead(prev => ({ ...prev, priority: nextPriority }));
    }
  };

  // Action: Assign Lead
  const handleAssignLead = async (leadId, sourceTable, staffId) => {
    const assignedStaff = staffList.find(s => s.id === staffId);
    const staffName = assignedStaff ? assignedStaff.full_name : null;

    const { error } = await crmService.updateLead(leadId, sourceTable, {
      assigned_to: staffId || null,
      note: staffId ? `Assigned lead to ${staffName}` : 'Unassigned lead',
      activity_type: 'assignment'
    });

    if (error) {
      alert(error.message || 'Failed to assign lead');
      return;
    }

    setLeads(prev => prev.map(l => l.lead_id === leadId ? {
      ...l,
      assigned_to: staffId || null,
      assigned_to_name: staffName
    } : l));

    if (selectedLead && selectedLead.lead_id === leadId) {
      setSelectedLead(prev => ({
        ...prev,
        assigned_to: staffId || null,
        assigned_to_name: staffName
      }));
      const { data } = await crmService.getLeadActivities(leadId);
      setLeadActivities(data || []);
    }

    await logAction(user?.id, adminProfile?.full_name || 'Staff', 'CRM Assign Lead', leadId, staffId ? `Assigned to ${staffName}` : 'Unassigned');
  };

  // Action: Add Activity Note
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !selectedLead) return;

    setSavingNote(true);
    const { error } = await crmService.addActivity(selectedLead.lead_id, selectedLead.source_table, {
      activity_type: activityType,
      notes: newNote.trim(),
      new_status: selectedLead.status
    });

    setSavingNote(false);
    if (error) {
      alert(error.message || 'Failed to save note');
      return;
    }

    setNewNote('');
    // Refresh activities
    const { data } = await crmService.getLeadActivities(selectedLead.lead_id);
    setLeadActivities(data || []);
  };

  // Action: Add Scheduled Follow-up
  const handleCreateFollowup = async (e) => {
    e.preventDefault();
    if (!newFollowupTitle.trim() || !newFollowupDate || !selectedLead) return;

    setSavingFollowup(true);
    const assignedStaffId = selectedLead.assigned_to || user?.id;

    const { error } = await crmService.addFollowup({
      lead_id: selectedLead.lead_id,
      lead_source_table: selectedLead.source_table,
      assigned_to: assignedStaffId,
      task_title: newFollowupTitle.trim(),
      due_date: new Date(newFollowupDate).toISOString()
    });

    setSavingFollowup(false);
    if (error) {
      alert(error.message || 'Failed to schedule follow-up');
      return;
    }

    setNewFollowupTitle('');
    setNewFollowupDate('');
    // Refresh followups
    const { data } = await crmService.getFollowups();
    setFollowups(data || []);
    alert('Follow-up scheduled successfully!');
  };

  // Action: Complete Follow-up
  const handleCompleteFollowup = async (followupId) => {
    const { error } = await crmService.completeFollowup(followupId);
    if (error) {
      alert('Could not complete task');
      return;
    }
    setFollowups(prev => prev.map(f => f.id === followupId ? { ...f, is_completed: true } : f));
  };

  // Action: Export CSV
  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      alert('No leads to export');
      return;
    }

    const headers = [
      'Lead ID', 'Category', 'Source', 'Customer Name', 'Phone', 'Requirement',
      'Property / Context', 'City', 'Budget', 'Stage / Status', 'Priority',
      'Assigned To', 'Created Date'
    ];

    const rows = filteredLeads.map(l => [
      l.lead_id,
      `"${(l.lead_category || '').replace(/"/g, '""')}"`,
      l.source_channel || '',
      `"${(l.customer_name || '').replace(/"/g, '""')}"`,
      l.customer_phone || '',
      `"${(l.requirement || '').replace(/"/g, '""')}"`,
      `"${(l.property_title || '').replace(/"/g, '""')}"`,
      l.city || '',
      `"${(l.budget || '').replace(/"/g, '""')}"`,
      l.status || 'New',
      l.priority || 'Warm',
      `"${(l.assigned_to_name || 'Unassigned').replace(/"/g, '""')}"`,
      new Date(l.created_at).toLocaleString()
    ]);

    const csvContent = "data:text/csv;charset=utf-8,"
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `crm_pipeline_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    loading,
    leads,
    filteredLeads,
    staffList,
    followups,
    todaysFollowups,
    kanbanColumns,
    viewMode,
    setViewMode,
    selectedLead,
    handleSelectLead,
    leadActivities,
    activitiesLoading,
    search,
    setSearch,
    categoryFilter,
    setCategoryFilter,
    cityFilter,
    setCityFilter,
    stageFilter,
    setStageFilter,
    priorityFilter,
    setPriorityFilter,
    assignFilter,
    setAssignFilter,
    dateFilter,
    setDateFilter,
    newNote,
    setNewNote,
    activityType,
    setActivityType,
    savingNote,
    handleAddNote,
    newFollowupTitle,
    setNewFollowupTitle,
    newFollowupDate,
    setNewFollowupDate,
    savingFollowup,
    handleCreateFollowup,
    handleCompleteFollowup,
    handleUpdateStatus,
    handleUpdatePriority,
    handleAssignLead,
    handleExportCSV,
    fetchAllData,
    currentUser: user,
    currentAdminProfile: adminProfile
  };
};

